import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor for logging and adding common headers
 */
apiClient.interceptors.request.use(
  (config) => {
    // Add JWT token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request in development, but skip endpoints that commonly return expected 404s
    if (process.env.NODE_ENV === 'development') {
      const url = config.url || '';
      // Don't log requests to endpoints that often result in expected 404s
      const isExpected404Endpoint = url.includes('/preferences/') || 
                                    url.includes('/pregnancy/profile/') ||
                                    url.includes('/health-profile/summary/');
      if (!isExpected404Endpoint) {
        console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`, config.params || config.data);
      }
    }
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for handling errors and logging
 */
apiClient.interceptors.response.use(
  (response) => {
      // Log response in development, but skip endpoints that commonly return expected 404s
    if (process.env.NODE_ENV === 'development') {
      const url = response.config?.url || '';
      // Don't log responses from endpoints that often result in expected 404s
      const isExpected404Endpoint = url.includes('/preferences/') || 
                                    url.includes('/pregnancy/profile/') ||
                                    url.includes('/health-profile/summary/');
      if (!isExpected404Endpoint) {
        console.log(`📥 ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
      }
    }
    
    // Handle standardized API response format
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (response.data.success) {
        return response.data.data !== undefined ? response.data.data : response.data;
      } else {
        // API returned error response
        const error = new Error(response.data.message || response.data.error || 'An error occurred');
        error.response = response;
        error.data = response.data; // Include full response data
        error.errorCode = response.data.errorCode;
        return Promise.reject(error);
      }
    }
    
    return response.data;
  },
  (error) => {
      // Handle axios errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      // Special handling for chunked encoding errors with 200 status
      // This means the request succeeded but response parsing failed
      if (status === 200 && (error.message?.includes('chunked') || error.code === 'ERR_INVALID_CHUNKED_ENCODING')) {
        console.warn('⚠️ Got 200 status but chunked encoding error - request likely succeeded');
        // Return a success indicator so the caller can handle it
        const chunkedError = new Error('Response parsing failed but request succeeded (200 OK)');
        chunkedError.status = 200;
        chunkedError.isChunkedError = true;
        chunkedError.response = error.response;
        return Promise.reject(chunkedError);
      }
      
      // Check if this is an expected 404 BEFORE logging anything
      const url = error.config?.url || '';
      const fullUrl = error.config?.url || error.request?.responseURL || '';
      const requestUrl = error.config?.url || error.request?.responseURL || '';
      const isExpected404 = status === 404 && (
        url.includes('/preferences/') || url.includes('/pregnancy/profile/') ||
        url.includes('/health-profile/summary/') ||
        fullUrl.includes('/preferences/') || fullUrl.includes('/pregnancy/profile/') ||
        fullUrl.includes('/health-profile/summary/') ||
        requestUrl.includes('/health-profile/summary/')
      );
      
      // Try to extract message from standardized response
      let message = 'An error occurred';
      if (data && typeof data === 'object') {
        if (data.message) {
          message = data.message;
        } else if (data.error) {
          message = data.error;
        } else if (data.data && typeof data.data === 'string') {
          message = data.data;
        } else if (typeof data === 'string') {
          message = data;
        }
        // Check for nested error messages
        if (data.metadata && data.metadata.errors) {
          const errors = data.metadata.errors;
          if (typeof errors === 'object') {
            const errorMessages = Object.values(errors).join(', ');
            if (errorMessages) {
              message = errorMessages;
            }
          }
        }
        // Only log full error response for debugging if it's NOT an expected 404
        if (process.env.NODE_ENV === 'development' && !isExpected404) {
          console.error('Full error response:', JSON.stringify(data, null, 2));
        }
      }
      
      // 404 is often expected (resource doesn't exist yet) - log quietly
      if (status === 404) {
        
        if (isExpected404) {
          // Silently handle expected 404s - these are normal when user hasn't set up data yet
          // Don't log at all for these endpoints - suppress completely
          // Return the error silently without any console output
          // Suppress the "Full error response" log as well
          const enhancedError = new Error(message);
          enhancedError.status = status;
          enhancedError.response = error.response;
          enhancedError.data = data;
          enhancedError.errorCode = data?.errorCode || `HTTP_${status}`;
          enhancedError.suppressLogging = true; // Flag to suppress logging
          return Promise.reject(enhancedError);
        } else if (process.env.NODE_ENV === 'development') {
          // Log other 404s quietly in development
          console.log(`ℹ️ API [${status}]:`, message, '(Expected when resource doesn\'t exist yet)');
        }
      } else {
        console.error(`❌ API Error [${status}]:`, message);
      }
      
      // Handle 401 Unauthorized - clear auth and redirect to login
      if (status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        // Only redirect if not already on login/register page
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          window.location.href = '/login';
        }
      }
      
      // Create enhanced error object
      const enhancedError = new Error(message);
      enhancedError.status = status;
      enhancedError.response = error.response;
      enhancedError.data = data;
      enhancedError.errorCode = data?.errorCode || `HTTP_${status}`;
      
      return Promise.reject(enhancedError);
    } else if (error.request) {
      // Request was made but no response received OR response parsing failed
      // Check if this is a chunked encoding error (status 200 but parsing failed)
      const isChunkedError = error.message?.includes('chunked') || 
                            error.code === 'ERR_INVALID_CHUNKED_ENCODING' ||
                            (error.request?.status === 200) ||
                            (error.request?.readyState === 4 && error.request?.status === 200);
      
      if (isChunkedError) {
        console.warn('⚠️ Chunked encoding error detected (200 OK but parsing failed)');
        const chunkedError = new Error('Response parsing failed but request succeeded (200 OK)');
        chunkedError.status = 200;
        chunkedError.isChunkedError = true;
        chunkedError.isNetworkError = false;
        chunkedError.request = error.request;
        return Promise.reject(chunkedError);
      }
      
      console.error('❌ No response received:', error.request);
      const networkError = new Error('Network error: Unable to reach server');
      networkError.isNetworkError = true;
      networkError.request = error.request;
      return Promise.reject(networkError);
    } else {
      // Something else happened
      console.error('❌ Request setup error:', error.message);
      return Promise.reject(error);
    }
  }
);

export default apiClient;

